import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { toast } from "sonner";
import { Plus, Calendar, Trash, Lightning } from "@phosphor-icons/react";

const KINDS = [
  { value: "audiencia_publica", label: "Audiência pública" },
  { value: "sessao", label: "Sessão" },
  { value: "visita", label: "Visita" },
  { value: "reuniao_aberta", label: "Reunião aberta" },
  { value: "evento_comunitario", label: "Evento comunitário" },
  { value: "outro", label: "Outro compromisso" },
];

/**
 * MandateAgenda — agenda pública do(s) representante(s). Cada evento
 * registra data/local/tema; cidadãos opt-in no tema recebem aviso
 * dentro da janela (min_notice_hours ≤ aviso ≤ max_notice_hours antes).
 *
 * Botão "Testar agora" roda `dispatch-now` para QA.
 */
export default function MandateAgenda() {
  const [events, setEvents] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    mandate_id: "", title: "", kind: "audiencia_publica",
    starts_at: "", location: "", topic: "", source_url: "",
    min_notice_hours: 24, max_notice_hours: 72,
  });
  const [acc, setAcc] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const [{ data: e }, { data: r }, { data: a }] = await Promise.all([
        api.get("/mandate/agenda"),
        api.get("/mandate/representatives"),
        api.get("/mandate/accountability/settings"),
      ]);
      setEvents(e?.events || []);
      setReps(r?.representatives || []);
      setAcc(a || null);
    } catch {
      toast.error("Não foi possível carregar.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function submit() {
    if (!form.title || !form.starts_at || !form.topic) {
      toast.error("Preencha título, data/hora e tema.");
      return;
    }
    try {
      const payload = { ...form, starts_at: new Date(form.starts_at).toISOString() };
      await api.post("/mandate/agenda", payload);
      toast.success("Compromisso agendado.");
      setShowNew(false);
      setForm({ ...form, title: "", starts_at: "", location: "", topic: "", source_url: "" });
      await reload();
    } catch {
      toast.error("Falha ao agendar.");
    }
  }

  async function dispatchNow() {
    try {
      const { data } = await api.post("/mandate/agenda/dispatch-now");
      toast.success(`Rodada manual: ${data?.counters?.sent || 0} enviados · ${data?.counters?.out_of_window || 0} fora da janela.`);
    } catch {
      toast.error("Falha no disparo manual.");
    }
  }

  async function remove(id) {
    if (!window.confirm("Remover este compromisso da agenda?")) return;
    try {
      await api.delete(`/mandate/agenda/${id}`);
      await reload();
    } catch {
      toast.error("Falha ao remover.");
    }
  }

  async function saveAcc() {
    if (!acc) return;
    try {
      const { data } = await api.put("/mandate/accountability/settings", {
        frequency: acc.frequency,
        day_of_week: Number(acc.day_of_week || 1),
        day_of_month: Number(acc.day_of_month || 1),
        hour_utc: Number(acc.hour_utc || 13),
        lookback_days: Number(acc.lookback_days || 30),
      });
      setAcc(data);
      toast.success("Preferência salva.");
    } catch {
      toast.error("Falha ao salvar.");
    }
  }

  async function dispatchAccNow() {
    try {
      const { data } = await api.post("/mandate/accountability/dispatch-now");
      toast.success(`Prestação de contas: ${data?.counters?.sent || 0} enviadas · ${data?.counters?.citizens || 0} cidadãos avaliados.`);
    } catch {
      toast.error("Falha no envio manual.");
    }
  }

  function repName(id) {
    const r = reps.find((x) => x.id === id);
    return r?.name || "— sem representante";
  }

  return (
    <div className="space-y-6" data-testid="mandate-agenda-page">
      <Topbar title="Agenda & Prestação de Contas" subtitle="Compromissos públicos e resumos factuais que o Agentus envia aos cidadãos opt-in." />

      {/* AGENDA */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Agenda pública</h2>
            <p className="text-xs text-slate-500">O Coordenador avisa só cidadãos com opt-in no tema, dentro da janela de antecedência.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={dispatchNow} data-testid="mandate-agenda-dispatch-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              <Lightning size={14} /> Rodar agora
            </button>
            <button onClick={() => setShowNew(true)} data-testid="mandate-agenda-new-btn" className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
              <Plus size={14} weight="bold" /> Novo compromisso
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-4">Carregando...</div>
        ) : events.length === 0 ? (
          <div className="rounded border border-dashed border-slate-300 p-8 text-center">
            <Calendar size={28} className="mx-auto mb-2 text-slate-400" />
            <p className="text-slate-800 font-medium">Nenhum compromisso cadastrado.</p>
            <p className="text-sm text-slate-500 mt-1">Agende audiências, sessões, visitas ou eventos comunitários.</p>
          </div>
        ) : (
          <ul className="divide-y border rounded">
            {events.map((e) => (
              <li key={e.id} className="p-3 flex items-start gap-3" data-testid={`mandate-agenda-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-500 uppercase tracking-wide">
                    {KINDS.find((k) => k.value === e.kind)?.label || e.kind}
                  </div>
                  <div className="text-slate-900 font-medium">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(e.starts_at).toLocaleString("pt-BR")} · {repName(e.mandate_id)}
                    {e.location ? ` · ${e.location}` : ""}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    Tema: <span className="font-medium">{e.topic}</span> · janela {e.min_notice_hours}h–{e.max_notice_hours}h · status: {e.status}
                  </div>
                </div>
                <button onClick={() => remove(e.id)} className="text-slate-500 hover:text-red-600">
                  <Trash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* PRESTAÇÃO DE CONTAS */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Prestação de contas</h2>
            <p className="text-xs text-slate-500">Resumo factual recorrente para cidadãos opt-in — zero fabricação, só o que está na base.</p>
          </div>
          <button onClick={dispatchAccNow} data-testid="mandate-accountability-dispatch-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
            <Lightning size={14} /> Enviar agora
          </button>
        </div>

        {acc ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-xs text-slate-600">
              Frequência
              <select
                data-testid="mandate-acc-frequency"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={acc.frequency}
                onChange={(e) => setAcc({ ...acc, frequency: e.target.value })}
              >
                <option value="never">Desativada</option>
                <option value="weekly">Semanal</option>
                <option value="biweekly">Quinzenal</option>
                <option value="monthly">Mensal</option>
              </select>
            </label>
            {acc.frequency === "monthly" ? (
              <label className="text-xs text-slate-600">
                Dia do mês
                <input type="number" min={1} max={28}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  value={acc.day_of_month}
                  onChange={(e) => setAcc({ ...acc, day_of_month: e.target.value })}
                />
              </label>
            ) : (
              <label className="text-xs text-slate-600">
                Dia da semana
                <select
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  value={acc.day_of_week}
                  onChange={(e) => setAcc({ ...acc, day_of_week: e.target.value })}
                >
                  {["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"]
                    .map((d, i) => (<option key={i} value={i}>{d}</option>))}
                </select>
              </label>
            )}
            <label className="text-xs text-slate-600">
              Hora UTC
              <input type="number" min={0} max={23}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={acc.hour_utc}
                onChange={(e) => setAcc({ ...acc, hour_utc: e.target.value })}
              />
            </label>
            <label className="text-xs text-slate-600">
              Olhar últimos (dias)
              <input type="number" min={1} max={365}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={acc.lookback_days}
                onChange={(e) => setAcc({ ...acc, lookback_days: e.target.value })}
              />
            </label>
            <div className="md:col-span-4 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Último envio: {acc.last_dispatched_at ? new Date(acc.last_dispatched_at).toLocaleString("pt-BR") : "nunca"}
              </div>
              <button onClick={saveAcc} data-testid="mandate-acc-save-btn" className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
                Salvar preferência
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {/* Modal novo evento */}
      {showNew ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-6 space-y-3" data-testid="mandate-agenda-form">
            <h3 className="text-lg font-semibold text-slate-900">Novo compromisso</h3>
            <select
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              value={form.mandate_id}
              onChange={(e) => setForm({ ...form, mandate_id: e.target.value })}
            >
              <option value="">Sem representante específico (institucional)</option>
              {reps.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
            </select>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Título (ex: Audiência sobre UBS Centro)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <select
                className="rounded border border-slate-300 px-3 py-2 text-sm"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                {KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
              </select>
              <input
                type="datetime-local"
                className="rounded border border-slate-300 px-3 py-2 text-sm"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
              />
            </div>
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Local (opcional)"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Tema (casa com interesses opt-in dos cidadãos)"
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
            />
            <input
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="Fonte / link oficial (opcional)"
              value={form.source_url}
              onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-600">
                Aviso mínimo antes (h)
                <input type="number" min={1} max={168}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  value={form.min_notice_hours}
                  onChange={(e) => setForm({ ...form, min_notice_hours: Number(e.target.value) })}
                />
              </label>
              <label className="text-xs text-slate-600">
                Aviso máximo antes (h)
                <input type="number" min={1} max={720}
                  className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                  value={form.max_notice_hours}
                  onChange={(e) => setForm({ ...form, max_notice_hours: Number(e.target.value) })}
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
              <button onClick={submit} data-testid="mandate-agenda-save-btn" className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Agendar</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
